import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { trpc } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumericInput } from "@/components/ui/numeric-input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  SlimTabs,
  SlimTabsList,
  SlimTabsTrigger,
} from "@/components/ui/slim-tabs";
import {
  Search,
  PlusIcon,
  HashIcon,
  TypeIcon,
  CheckSquareIcon,
  LockIcon,
  TrashIcon,
} from "lucide-react";
import { useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAutosave, filterDefined } from "@/hooks/use-autosave";
import { useCan } from "@/hooks/use-operator";
import { FeedLinesTab } from "@/features/fermentation-registry/feed-lines-tab";
import {
  FermParameterKindEnum,
  FermTextInputModeEnum,
  ParameterValueSourceEnum,
} from "@brewbase/db/types";
import type { FermentationParameterType, FermentationParameterTypeId, InvResourceId, InvResourceCategoryId } from "@brewbase/db/types";
import { ResourceSelect } from "@/features/inventory/resource-select";
import { z } from "zod";
import { TELEMETRY_UNITS, convertTelemetryUnit } from "@brewbase/lib";
import { useSignalUnits } from "@/features/fermentation-live/use-signal-units";
import { Combobox } from "@/components/ui/combobox";
import { Switch } from "@/components/ui/switch";
import { useZodForm } from "@/components/form";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/settings/fermentation-parameters")({
  component: RegistryPage,
  // The tab is in the URL so a page that sends an operator here -- the
  // procedure's lines page, say -- can land them on the right registry.
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: "parameters" | "lines" } =>
    search.tab === "lines" ? { tab: "lines" } : {},
});

/**
 * The vocabulary a fermentation is authored against: the values an operator
 * records (parameter types) and the plumbing they are recorded on (feed
 * lines). Two registries, one page -- they share nothing but that role, so
 * they stay two lists rather than one table of mostly-blank columns.
 */
function RegistryPage() {
  const { tab = "parameters" } = Route.useSearch();
  const navigate = useNavigate();

  return (
    <div className="container mx-auto px-4 py-6 flex flex-col grow min-h-0 gap-4">
      <div>
        <h1 className="text-lg font-semibold">Parameters &amp; Feed Lines</h1>
        <p className="text-sm text-muted-foreground">
          The parameter types protocols and phases author values for, and the
          feed lines those values drive. System entries are built-in and cannot
          be deleted.
        </p>
      </div>

      <SlimTabs
        value={tab}
        onValueChange={(value) =>
          navigate({ to: ".", search: { tab: value as typeof tab } })
        }
      >
        <SlimTabsList>
          <SlimTabsTrigger value="parameters">Parameters</SlimTabsTrigger>
          <SlimTabsTrigger value="lines">Feed Lines</SlimTabsTrigger>
        </SlimTabsList>
      </SlimTabs>

      {tab === "parameters" ? <ParameterTypesPage /> : <FeedLinesTab />}
    </div>
  );
}

const KIND_CONFIG: Record<
  FermParameterKindEnum,
  { label: string; icon: React.ReactNode }
> = {
  [FermParameterKindEnum.NUMBER]: {
    label: "Number",
    icon: <HashIcon className="h-3.5 w-3.5" />,
  },
  [FermParameterKindEnum.TEXT]: {
    label: "Text",
    icon: <TypeIcon className="h-3.5 w-3.5" />,
  },
  [FermParameterKindEnum.CHECKBOX]: {
    label: "Checkbox",
    icon: <CheckSquareIcon className="h-3.5 w-3.5" />,
  },
};

const newParameterTypeSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    code: z
      .string()
      .min(1, "Code is required")
      .regex(/^[a-z][a-z0-9_]*$/, "Code must be lowercase with underscores"),
    description: z.string().optional(),
    kind: z.nativeEnum(FermParameterKindEnum),
    // The canonical unit (decision 0001): the one the node holds the setpoint in.
    unit: z.string().optional(),
    ramps: z.boolean(),
    valueSource: z.nativeEnum(ParameterValueSourceEnum),
    sourceAttribute: z.string().optional(),
    minValue: z.string().optional(),
    maxValue: z.string().optional(),
    textInputMode: z.nativeEnum(FermTextInputModeEnum).optional(),
    category: z.string().optional(),
  })
  .refine(
    (v) => v.kind !== FermParameterKindEnum.NUMBER || !!v.unit?.trim(),
    { message: "A number parameter needs a unit", path: ["unit"] },
  )
  .refine(
    (v) => v.valueSource !== ParameterValueSourceEnum.measured || !!v.sourceAttribute,
    { message: "A measured parameter reads an attribute", path: ["sourceAttribute"] },
  );

const UNIT_OPTIONS = Object.keys(TELEMETRY_UNITS).map((unit) => ({
  value: unit,
  label: unit,
  labelString: `${unit} ${TELEMETRY_UNITS[unit].dimension}`,
  group: TELEMETRY_UNITS[unit].dimension.replaceAll("_", " "),
}));

/**
 * The canonical unit: one of the registry's units, or free text for a unit
 * the registry does not know. An unknown unit is allowed but says so, since
 * nothing will convert it for display.
 */
function UnitField({
  value,
  onChange,
  id,
}: {
  value: string;
  onChange: (unit: string) => void;
  id?: string;
}) {
  const known = value === "" || value in TELEMETRY_UNITS;
  return (
    <div className="space-y-1">
      <Combobox
        aria-label="Unit"
        options={UNIT_OPTIONS}
        value={value}
        placeholder={value || "Choose or type a unit"}
        triggerClassName={`w-full font-mono ${id ?? ""}`}
        onValueChange={onChange}
        onCreateNew={(text) => onChange(text.trim())}
      />
      {!known && (
        <p className="inline-block bg-warning px-1 text-xs text-warning-foreground">
          unknown unit: it will be stored as written and never converted
        </p>
      )}
    </div>
  );
}

const VALUE_SOURCES: Array<{ value: ParameterValueSourceEnum; label: string; help: string }> = [
  { value: ParameterValueSourceEnum.authored, label: "Authored", help: "Typed by the protocol or the operator." },
  { value: ParameterValueSourceEnum.prompted, label: "Prompted", help: "Asked of the operator when a protocol is applied without a value." },
  { value: ParameterValueSourceEnum.measured, label: "Measured", help: "Read from a telemetry attribute when a protocol is applied without a value." },
];

/**
 * Where a number's value comes from when a protocol puts it in force without
 * one. A measured type reads an attribute whose unit converts to its own; a
 * reading is a step, so a measured type never ramps.
 */
function ValueSourceField({
  unit,
  valueSource,
  sourceAttribute,
  onChange,
}: {
  unit: string | null;
  valueSource: ParameterValueSourceEnum;
  sourceAttribute: string | null;
  onChange: (next: { valueSource: ParameterValueSourceEnum; sourceAttribute: string | null }) => void;
}) {
  const { data: attributes = [] } = trpc.fermentationData.getAttributeDefinitions.useQuery(undefined, {
    enabled: valueSource === ParameterValueSourceEnum.measured,
    staleTime: 5 * 60_000,
  });
  const options = useMemo(
    () =>
      attributes
        .filter((a) => !unit || convertTelemetryUnit(1, a.unit, unit) !== null)
        .map((a) => ({
          value: a.name,
          label: `${a.display_name} (${a.unit ?? "no unit"})`,
          labelString: `${a.display_name} ${a.name}`,
          group: a.category ?? "other",
        })),
    [attributes, unit],
  );
  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Value source</Label>
        <Select
          value={valueSource}
          onValueChange={(v) =>
            onChange({
              valueSource: v as ParameterValueSourceEnum,
              sourceAttribute: v === ParameterValueSourceEnum.measured ? sourceAttribute : null,
            })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VALUE_SOURCES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {VALUE_SOURCES.find((s) => s.value === valueSource)?.help}
        </p>
      </div>
      {valueSource === ParameterValueSourceEnum.measured && (
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Reads</Label>
          <Combobox
            aria-label="Attribute"
            options={options}
            value={sourceAttribute ?? ""}
            placeholder={sourceAttribute ?? "Choose an attribute"}
            triggerClassName="w-full font-mono"
            onValueChange={(name) => onChange({ valueSource, sourceAttribute: name })}
          />
        </div>
      )}
    </div>
  );
}


function ParameterTypesPage() {
  const utils = trpc.useUtils();
  const canEdit = useCan("fermentation-registry:edit");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState<string>("ALL");
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [selectedId, setSelectedId] =
    useState<FermentationParameterTypeId | null>(null);

  const { data: parameterTypes, isLoading } =
    trpc.fermentationParameterTypes.list.useQuery();

  const { data: categories } =
    trpc.fermentationParameterTypes.getCategories.useQuery();

  const { data: resourceCategories } =
    trpc.inventory.resource.listCategories.useQuery();

  const createMutation = trpc.fermentationParameterTypes.create.useMutation({
    onSuccess: (data) => {
      toast.success(`Parameter type "${data.name}" created!`);
      utils.fermentationParameterTypes.list.invalidate();
      utils.fermentationParameterTypes.getCategories.invalidate();
      setIsCreateSheetOpen(false);
      form.reset();
    },
    onError: (error) => {
      toast.error(`Failed to create: ${error.message}`);
    },
  });

  const deleteMutation = trpc.fermentationParameterTypes.delete.useMutation({
    onSuccess: () => {
      toast.success("Parameter type deleted");
      utils.fermentationParameterTypes.list.invalidate();
      utils.fermentationParameterTypes.getCategories.invalidate();
      setSelectedId(null);
    },
    onError: (error) => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });

  const form = useZodForm({
    schema: newParameterTypeSchema,
    defaultValues: {
      name: "",
      code: "",
      description: "",
      kind: FermParameterKindEnum.NUMBER,
      unit: "",
      ramps: true,
      valueSource: ParameterValueSourceEnum.authored,
      sourceAttribute: "",
      minValue: "",
      maxValue: "",
      category: "",
    },
  });

  const watchKind = form.watch("kind");

  // Filter and group parameter types
  const filteredTypes = useMemo(() => {
    if (!parameterTypes) return [];

    return parameterTypes.filter((pt) => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        pt.name.toLowerCase().includes(searchLower) ||
        pt.code.toLowerCase().includes(searchLower) ||
        (pt.description || "").toLowerCase().includes(searchLower);

      const matchesCategory =
        filterCategory === "ALL" ||
        (filterCategory === "SYSTEM" && pt.is_system) ||
        pt.category === filterCategory;

      return matchesSearch && matchesCategory;
    });
  }, [parameterTypes, searchQuery, filterCategory]);


  const handleCreate = form.handleSubmit((values) => {
    createMutation.mutate({
      name: values.name,
      code: values.code,
      description: values.description || null,
      kind: values.kind,
      unit: values.kind === FermParameterKindEnum.NUMBER ? values.unit?.trim() || null : null,
      ramps:
        values.kind === FermParameterKindEnum.NUMBER && values.valueSource !== ParameterValueSourceEnum.measured
          ? values.ramps
          : false,
      valueSource: values.kind === FermParameterKindEnum.NUMBER ? values.valueSource : ParameterValueSourceEnum.authored,
      sourceAttribute:
        values.kind === FermParameterKindEnum.NUMBER && values.valueSource === ParameterValueSourceEnum.measured
          ? values.sourceAttribute || null
          : null,
      minValue: values.minValue || null,
      maxValue: values.maxValue || null,
      textInputMode: values.textInputMode || null,
      category: values.category || null,
    });
  });

  const handleDelete = (id: FermentationParameterTypeId) => {
    if (confirm("Are you sure you want to delete this parameter type?")) {
      deleteMutation.mutate(id);
    }
  };

  const selectedType = parameterTypes?.find((pt) => pt.id === selectedId);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-muted-foreground">
            Loading parameter types...
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Search and Actions */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search parameter types..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {canEdit && (
          <Button onClick={() => setIsCreateSheetOpen(true)}>
            <PlusIcon className="w-4 h-4 mr-1" />
            New Type
          </Button>
        )}
      </div>

      {/* Category Filter Tabs */}
      <div className="mb-4">
        <SlimTabs value={filterCategory} onValueChange={setFilterCategory}>
          <SlimTabsList>
            <SlimTabsTrigger value="ALL">
              All ({parameterTypes?.length ?? 0})
            </SlimTabsTrigger>
            <SlimTabsTrigger value="SYSTEM">
              System ({parameterTypes?.filter((pt) => pt.is_system).length ?? 0}
              )
            </SlimTabsTrigger>
            {categories?.map((cat) => (
              <SlimTabsTrigger key={cat} value={cat}>
                {cat}
              </SlimTabsTrigger>
            ))}
          </SlimTabsList>
        </SlimTabs>
      </div>

      {/* Parameter Type List */}
      <div className="divide-y divide-border rounded-md border">
        {filteredTypes.map((pt) => (
          <ParameterTypeRow
            key={pt.id}
            parameterType={pt}
            onSelect={() => setSelectedId(pt.id)}
            onDelete={
              canEdit && !pt.is_system
                ? () => handleDelete(pt.id)
                : undefined
            }
            isSelected={selectedId === pt.id}
          />
        ))}

        {filteredTypes.length === 0 && (
          <div className="text-center py-8">
            <p className="text-muted-foreground text-sm">
              {searchQuery
                ? "No parameter types found matching your search."
                : "No parameter types in this category."}
            </p>
          </div>
        )}
      </div>

      {/* Create Sheet */}
      <Sheet open={isCreateSheetOpen} onOpenChange={setIsCreateSheetOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Create Parameter Type</SheetTitle>
            <SheetDescription>
              Define a new parameter type for fermentation protocols.
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={handleCreate} className="space-y-4 py-4">
            {/* Identity */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., OD Target"
                  {...form.register("name")}
                />
                {form.formState.errors.name && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.name.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="code">Code *</Label>
                <Input
                  id="code"
                  placeholder="e.g., od_target"
                  {...form.register("code")}
                />
                <p className="text-xs text-muted-foreground">
                  Unique identifier. Lowercase letters, numbers, and
                  underscores.
                </p>
                {form.formState.errors.code && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.code.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Describe this parameter type..."
                  {...form.register("description")}
                  rows={2}
                />
              </div>
            </div>

            {/* Type Configuration */}
            <div className="border-t pt-4 space-y-4">
              <div className="space-y-2">
                <Label>Kind *</Label>
                <div className="flex gap-3">
                  {Object.entries(KIND_CONFIG).map(([kind, config]) => (
                    <label
                      key={kind}
                      className="flex items-center gap-1.5 text-sm cursor-pointer"
                    >
                      <input
                        type="radio"
                        value={kind}
                        checked={watchKind === kind}
                        onChange={() =>
                          form.setValue("kind", kind as FermParameterKindEnum)
                        }
                        className="w-3 h-3"
                      />
                      <span className="flex items-center gap-1">
                        {config.icon}
                        {config.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Number-specific options */}
              {watchKind === FermParameterKindEnum.NUMBER && (
                <>
                  <div className="space-y-2">
                    <Label>Unit *</Label>
                    <UnitField
                      value={form.watch("unit") ?? ""}
                      onChange={(unit) =>
                        form.setValue("unit", unit, { shouldValidate: true })
                      }
                    />
                    {form.formState.errors.unit && (
                      <p className="text-xs text-destructive">
                        {form.formState.errors.unit.message}
                      </p>
                    )}
                  </div>
                  <ValueSourceField
                    unit={form.watch("unit") || null}
                    valueSource={form.watch("valueSource")}
                    sourceAttribute={form.watch("sourceAttribute") || null}
                    onChange={(next) => {
                      form.setValue("valueSource", next.valueSource);
                      form.setValue("sourceAttribute", next.sourceAttribute ?? "", { shouldValidate: true });
                    }}
                  />
                  {form.formState.errors.sourceAttribute && (
                    <p className="text-xs text-destructive">
                      {form.formState.errors.sourceAttribute.message}
                    </p>
                  )}
                  {form.watch("valueSource") !== ParameterValueSourceEnum.measured && (
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <Label>Ramps</Label>
                        <p className="text-xs text-muted-foreground">
                          Applying a protocol with a transition ramps this
                          parameter to its target; off, it steps.
                        </p>
                      </div>
                      <Switch
                        checked={form.watch("ramps")}
                        onCheckedChange={(checked) =>
                          form.setValue("ramps", checked)
                        }
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="minValue">Min Value</Label>
                      <NumericInput
                        precise
                        id="minValue"
                        placeholder="Optional"
                        value={form.watch("minValue") || null}
                        onValueChange={(v) =>
                          form.setValue("minValue", v ?? "")
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="maxValue">Max Value</Label>
                      <NumericInput
                        precise
                        id="maxValue"
                        placeholder="Optional"
                        value={form.watch("maxValue") || null}
                        onValueChange={(v) =>
                          form.setValue("maxValue", v ?? "")
                        }
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Text-specific options */}
              {watchKind === FermParameterKindEnum.TEXT && (
                <div className="space-y-2">
                  <Label>Input Mode</Label>
                  <Select
                    value={form.watch("textInputMode") ?? ""}
                    onValueChange={(value) =>
                      form.setValue(
                        "textInputMode",
                        value as FermTextInputModeEnum,
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select input mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={FermTextInputModeEnum.FREE_TEXT}>
                        Free text
                      </SelectItem>
                      <SelectItem value={FermTextInputModeEnum.DROPDOWN}>
                        Dropdown
                      </SelectItem>
                      <SelectItem value={FermTextInputModeEnum.AUTOCOMPLETE}>
                        Autocomplete
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Feed-specific options moved to the addition line registry:
                  a line's material, category and drive belong to the plumbing,
                  not to a parameter type. */}
            </div>

            {/* Organization */}
            <div className="border-t pt-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  placeholder="e.g., Process Setpoints, Feed Rates"
                  {...form.register("category")}
                />
              </div>
            </div>

            <SheetFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateSheetOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? "Creating..." : "Create Type"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Detail Sheet */}
      <Sheet open={!!selectedType} onOpenChange={() => setSelectedId(null)}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          {selectedType && (
            <ParameterTypeDetail
              key={selectedType.id}
              parameterType={selectedType}
              resourceCategories={resourceCategories}
              onDelete={
                canEdit && !selectedType.is_system
                  ? () => handleDelete(selectedType.id)
                  : undefined
              }
              isDeletePending={deleteMutation.isPending}
            />
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

// ======================== Parameter Type Detail ========================

interface ParameterTypeDetailProps {
  parameterType: FermentationParameterType;
  resourceCategories:
    | { id: InvResourceCategoryId; name: string }[]
    | undefined;
  onDelete?: () => void;
  isDeletePending: boolean;
}

function ParameterTypeDetail({
  parameterType: pt,
  resourceCategories,
  onDelete,
  isDeletePending,
}: ParameterTypeDetailProps) {
  const { data: current } = trpc.fermentationParameterTypes.get.useQuery(pt.id);
  const [choosingMeasured, setChoosingMeasured] = useState(false);
  // Use query data for optimistic updates, fall back to prop for initial render
  const data = current ?? pt;
  // The unit is canonical (decision 0001); the display unit is what operators
  // read and type, resolved per procedure with a per-dimension default.
  const units = useSignalUnits();
  const shownAs = units.forParameter(data.code, data.unit);

  const { save } = useAutosave({
    mutation: trpc.fermentationParameterTypes.update,
    query: trpc.fermentationParameterTypes.get,
    queryInput: pt.id,
    updateCache: (input, old) =>
      old
        ? {
            ...old,
            ...filterDefined({
              name: input.name,
              description: input.description,
              unit: input.unit,
              ramps: input.valueSource === ParameterValueSourceEnum.measured ? false : input.ramps,
              value_source: input.valueSource,
              source_attribute: input.sourceAttribute,
              min_value: input.minValue,
              max_value: input.maxValue,
              category: input.category,
              text_input_mode: input.textInputMode,
            }),
          }
        : old,
    onError: (err) => toast.error(err.message),
  });

  const update = useCallback(
    (fields: Record<string, unknown>) => {
      save({ id: pt.id, ...fields } as Parameters<typeof save>[0]);
    },
    [save, pt.id],
  );

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          {data.name}
          {data.is_system && (
            <Badge variant="secondary" className="gap-1">
              <LockIcon className="h-3 w-3" />
              System
            </Badge>
          )}
        </SheetTitle>
        <SheetDescription>
          <code className="text-xs bg-muted px-1 py-0.5 rounded">
            {data.code}
          </code>
        </SheetDescription>
      </SheetHeader>

      <div className="py-4 space-y-4">
        {/* Name — disabled for system types */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Name</Label>
          <Input
            value={data.name}
            onChange={(e) => update({ name: e.target.value })}
            disabled={data.is_system}
          />
        </div>

        {/* Kind — read-only, cannot change */}
        <div>
          <Label className="text-xs text-muted-foreground">Kind</Label>
          <div className="flex items-center gap-1.5 mt-1">
            {KIND_CONFIG[data.kind].icon}
            <span className="text-sm">{KIND_CONFIG[data.kind].label}</span>
          </div>
        </div>

        {/* Description */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Description</Label>
          <Textarea
            value={data.description ?? ""}
            onChange={(e) =>
              update({ description: e.target.value || null })
            }
            rows={2}
            placeholder="Describe this parameter type..."
          />
        </div>

        {/* Number-specific fields */}
        {data.kind === FermParameterKindEnum.NUMBER && (
          <>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Unit</Label>
              <UnitField
                value={data.unit ?? ""}
                onChange={(unit) => {
                  if (unit) update({ unit });
                }}
              />
              {!data.unit && (
                <p className="text-xs text-destructive">
                  A number parameter needs a unit
                </p>
              )}
              {data.unit && shownAs && shownAs !== data.unit && (
                <p className="text-xs text-muted-foreground">
                  Canonical unit; every stored value is in it. Operators read and type it as{" "}
                  <span className="font-mono">{shownAs}</span> unless their procedure prefers otherwise.
                </p>
              )}
            </div>
            <ValueSourceField
              unit={data.unit}
              valueSource={choosingMeasured ? ParameterValueSourceEnum.measured : data.value_source}
              sourceAttribute={data.source_attribute}
              onChange={(next) => {
                // A measured type is saved once it names what it reads.
                const incomplete = next.valueSource === ParameterValueSourceEnum.measured && !next.sourceAttribute;
                setChoosingMeasured(incomplete);
                if (!incomplete) update(next);
              }}
            />
            {data.value_source !== ParameterValueSourceEnum.measured && (
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Ramps</Label>
                  <p className="text-xs text-muted-foreground">
                    Applying a protocol with a transition ramps this parameter
                    to its target; off, it steps.
                  </p>
                </div>
                <Switch
                  checked={data.ramps}
                  onCheckedChange={(ramps) => update({ ramps })}
                />
              </div>
            )}
            {data.kind === FermParameterKindEnum.NUMBER && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    Min Value
                  </Label>
                  <NumericInput
                    precise
                    value={data.min_value || null}
                    onValueChange={(v) => update({ minValue: v ?? null })}
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    Max Value
                  </Label>
                  <NumericInput
                    precise
                    value={data.max_value || null}
                    onValueChange={(v) => update({ maxValue: v ?? null })}
                    placeholder="Optional"
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* Text-specific fields */}
        {data.kind === FermParameterKindEnum.TEXT && (
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">
              Input Mode
            </Label>
            <Select
              value={data.text_input_mode ?? ""}
              onValueChange={(value) =>
                update({
                  textInputMode: (value as FermTextInputModeEnum) || null,
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select input mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FermTextInputModeEnum.FREE_TEXT}>
                  Free text
                </SelectItem>
                <SelectItem value={FermTextInputModeEnum.DROPDOWN}>
                  Dropdown
                </SelectItem>
                <SelectItem value={FermTextInputModeEnum.AUTOCOMPLETE}>
                  Autocomplete
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Feed-specific fields live on the addition line registry now. */}

        {/* Category */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Category</Label>
          <Input
            value={data.category ?? ""}
            onChange={(e) =>
              update({ category: e.target.value || null })
            }
            placeholder="e.g., Process Setpoints, Feed Rates"
          />
        </div>

        {/* Delete — non-system types only */}
        {onDelete && (
          <div className="border-t pt-4">
            <Button
              variant="destructive"
              size="sm"
              onClick={onDelete}
              disabled={isDeletePending}
            >
              <TrashIcon className="h-3 w-3 mr-1" />
              Delete Type
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

// ======================== Parameter Type Row ========================

interface ParameterTypeRowProps {
  parameterType: {
    id: FermentationParameterTypeId;
    name: string;
    code: string;
    kind: FermParameterKindEnum;
    unit: string | null;
    ramps: boolean;
    category: string | null;
    is_system: boolean;
  };
  onSelect: () => void;
  onDelete?: () => void;
  isSelected: boolean;
}

function ParameterTypeRow({
  parameterType,
  onSelect,
  onDelete,
  isSelected,
}: ParameterTypeRowProps) {
  const kindConfig = KIND_CONFIG[parameterType.kind];
  // The unit here is the canonical one every stored number is in (decision
  // 0001); operators read it in the display unit when that differs.
  const units = useSignalUnits();
  const shownAs = units.forParameter(parameterType.code, parameterType.unit);

  return (
    <div
      className={
        "group flex items-center justify-between px-4 py-2.5 hover:bg-muted/30 cursor-pointer transition-colors " +
        (isSelected ? "bg-primary/5" : "")
      }
      onClick={onSelect}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{parameterType.name}</span>
          <code className="text-xs bg-muted px-1 py-0.5 rounded text-muted-foreground">
            {parameterType.code}
          </code>
          {parameterType.is_system && (
            <LockIcon className="h-3 w-3 text-muted-foreground" />
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            {kindConfig.icon}
            {kindConfig.label}
          </span>
          {parameterType.unit && (
            <span className="font-mono">
              {parameterType.unit}
              {shownAs && shownAs !== parameterType.unit && (
                <span className="text-muted-foreground/70"> · shown as {shownAs}</span>
              )}
            </span>
          )}
          {parameterType.kind === FermParameterKindEnum.NUMBER &&
            parameterType.ramps && <span>ramps</span>}
          {parameterType.category && <span>{parameterType.category}</span>}
        </div>
      </div>

      {onDelete && !parameterType.is_system && (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="opacity-0 group-hover:opacity-100 h-7 w-7 p-0 hover:text-destructive hover:bg-destructive/10"
        >
          <TrashIcon className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}
